import { ErrorModal, HelmetDexteel } from "@dexteel/mesf-core";
import {
	Box,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	Divider,
	FormControlLabel,
	Grid2,
	TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

import { FooterModalButtons } from "@/controls/buttons/FooterModalButtons";
import {
	DetailHeader,
	DetailSection,
} from "@/controls/detail-panel/DetailPanel";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { EDITABLE_FIELD_SX } from "@/pages/reports/rollshop-operations/components/compactDialog";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
	DELAY_CATEGORY_INITIAL_VALUES,
	DelayCategory,
} from "../models/CatalogCategory";
import { upsertCatalogCategory } from "../repositories/CatalogCategoriesRepository";
import ColorPicker from "./ColorPicker/ColorPicker";

type Props = {
	delayCategory: DelayCategory | null;
	modal: "create" | "update" | "";
	onHide: () => void;
};

export const UpsertCategoryModal = ({
	delayCategory,
	modal,
	onHide,
}: Props) => {
	const [notificationMessage, setNotificationMessage] = useState("");
	const [error, setError] = useState("");
	const isEditing = modal === "update";

	const { control, reset, handleSubmit } = useForm<DelayCategory>({});

	const validateNoSpecialChars = (value: string) =>
		/^[a-zA-Z0-9 _-]*$/.test(value) || "No special characters";

	const queryClient = useQueryClient();
	const upsertMutation = useMutation(upsertCatalogCategory, {
		onSuccess: async () => {
			await queryClient.invalidateQueries(["delayCategories"]);
			setNotificationMessage(
				`The category was ${isEditing ? "updated" : "created"} successfully`,
			);
			onHide();
		},
		onError: (error: Error) => {
			setError(error.message);
		},
	});

	const onSubmit: SubmitHandler<DelayCategory> = (data) => {
		upsertMutation.mutate(data);
	};

	useEffect(() => {
		if (modal !== "") {
			const values =
				modal === "update" && delayCategory
					? delayCategory
					: DELAY_CATEGORY_INITIAL_VALUES;
			reset({ ...values, Description: values.Description ?? "" });
		}
	}, [modal, delayCategory]);

	const title = isEditing ? "Edit Stop Category" : "Add Stop Category";

	return (
		<>
			{modal !== "" && <HelmetDexteel title={`${title} - Stop Categories`} />}
			<Dialog open={modal !== ""} onClose={onHide} maxWidth="sm" fullWidth>
				<DetailHeader
					title={title}
					isSaving={upsertMutation.isLoading}
					onClose={onHide}
				/>
				<form onSubmit={handleSubmit(onSubmit)}>
					<DialogContent sx={{ p: 2 }}>
						<DetailSection title="Category" icon={<CategoryOutlinedIcon />}>
							<Grid2 container spacing={1}>
								<Grid2 size={{ xs: 12, md: 6 }}>
									<Controller
										name="DelayCategoryName"
										control={control}
										rules={{
											required: "Name is required",
											validate: validateNoSpecialChars,
										}}
										render={({ field, fieldState: { error } }) => (
											<TextField
												label="Name"
												value={field.value}
												onChange={field.onChange}
												error={!!error}
												helperText={error?.message}
												fullWidth
												required
												autoComplete="off"
												size="small"
												sx={EDITABLE_FIELD_SX}
												slotProps={{
													inputLabel: { shrink: true },
													htmlInput: { maxLength: 100 },
												}}
											/>
										)}
									/>
								</Grid2>
								<Grid2 size={{ xs: 12, md: 6 }}>
									<Controller
										name="DelayCategoryColor"
										control={control}
										rules={{ required: "Color is required" }}
										render={({ field, fieldState: { error } }) => (
											<Box
												sx={{
													display: "flex",
													alignItems: "center",
													gap: 1,
													minHeight: 40,
													"& .MuiButton-root": { mb: "0 !important" },
												}}
											>
												<ColorPicker
													onChange={(_: string, color: string) =>
														field.onChange(color)
													}
													color={field.value}
													label="Color"
												/>
												{error && (
													<span style={{ fontSize: 12, color: "#F44336" }}>
														{error.message}
													</span>
												)}
											</Box>
										)}
									/>
								</Grid2>
								<Grid2 size={{ xs: 12 }}>
									<Controller
										name="Description"
										control={control}
										render={({ field }) => (
											<TextField
												label="Description"
												value={field.value ?? ""}
												onChange={field.onChange}
												fullWidth
												multiline
												minRows={2}
												autoComplete="off"
												size="small"
												sx={EDITABLE_FIELD_SX}
												slotProps={{
													inputLabel: { shrink: true },
													htmlInput: { maxLength: 500 },
												}}
											/>
										)}
									/>
								</Grid2>
								<Grid2 size={{ xs: 12 }}>
									<Controller
										name="IsActive"
										control={control}
										render={({ field }) => (
											<FormControlLabel
												sx={{ userSelect: "none" }}
												control={
													<Checkbox
														checked={!!field.value}
														onChange={field.onChange}
														name="IsActive"
														color="primary"
														size="small"
													/>
												}
												label="Is Active"
											/>
										)}
									/>
								</Grid2>
							</Grid2>
						</DetailSection>
					</DialogContent>
					<Divider />
					<DialogActions sx={{ px: 2, py: 1.5 }}>
						<FooterModalButtons
							isSubmitLoading={upsertMutation.isLoading}
							onHide={onHide}
						/>
					</DialogActions>
				</form>
			</Dialog>
			<NotificationSnackBar
				message={notificationMessage}
				onHide={() => setNotificationMessage("")}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
